import { GetServerSideProps } from 'next'
import Head from 'next/head'
import { plainToInstance } from 'class-transformer'

import { CharacterPage } from '@features/character/pages/CharacterPage'
import { AppLayout } from '@components/layouts/AppLayout'
import { CharacterProps } from '@features/character/pages/CharacterPage/interface'
import { CharacterOrderEnum } from '@interfaces/CharacterOrderEnum'
import { gqlApiInstance } from '@client/init'
import { GET_CHARACTER_ROLE } from '@client/collections/Character/schemas/getCharacterRole'
import { BASE_URL } from '@configs/config'
import { OgShare } from '@components/OgShare'
import { CharacterRoleOptionType } from '@models/character/CharacterRoleOptionType'
import { isMaintenanceHandler } from '@lib/maintenanceHandler'
import { AppLinkMeta } from '@components/AppLinkMeta'

export default function Character({
  roleId,
  seoTitle,
  seoDescription,
  url,
  ...props
}: CharacterProps) {
  const characterUrl = roleId ? `?roleId=${roleId}` : ''

  return (
    <AppLayout seoDescription={seoDescription}>
      <Head>
        <title>{`MaReads มารี้ด | ${seoTitle}`}</title>
      </Head>
      <AppLinkMeta path={`character${characterUrl}`} webUrl={url} />
      <OgShare url={url} />
      <CharacterPage {...props} roleId={roleId} />
    </AppLayout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({
  query,
  req,
}) => {
  try {
    const characterRoleResponse = {
      seoTitle:
        'MaReads มารี้ด | จิ้น ฟิน ครบทุกรส ทั้งหญิงชาย วาย ยูริ และอีกมากมาย กับตัวละครในจักรวาลนักอ่านนักเขียน',
      seoDescription: `คู่จิ้น คลังนิยาย คลังการ์ตูน นิยายออนไลน์ นิยายแปล เว็บตูน รักโรแมนติก boy's love yuri แฟนตาซี และอีกมากมายได้ที่ MaReads จักรวาลของนักอ่านนักเขียน หรือซื้อรูปเล่มจากร้านนายอินทร์ออนไลน์หรือหนังสือในเครืออมรินทร์ได้เลย`,
    }
    const roleId = query.roleId ? Number(query.roleId) : 0

    const isMaintenance = await isMaintenanceHandler()
    if (isMaintenance) {
      return {
        redirect: {
          destination: '/maintenance',
          permanent: false,
        },
        props: {
          isMaintenance: !!isMaintenance,
        },
      }
    }

    if (roleId) {
      const response = await gqlApiInstance.request(GET_CHARACTER_ROLE, {
        id: roleId,
      })
      const characterRole = plainToInstance(
        CharacterRoleOptionType,
        response.characterRole
      )
      characterRoleResponse.seoTitle = characterRole.seoTitle
      characterRoleResponse.seoDescription = characterRole.seoDescription
    }

    return {
      props: {
        text: query.text || '',
        orderBy: query.orderBy || CharacterOrderEnum.LAST_ADDED,
        roleId,
        chapterType: query.chapterType || null,
        advanceSearch: query.advanceSearch === 'true',
        seoTitle: characterRoleResponse.seoTitle,
        seoDescription: characterRoleResponse.seoDescription,
        url: `${BASE_URL}${req.url}`,
        isMaintenance: !!isMaintenance,
      },
    }
  } catch (error: any) {
    if (error.response?.status === 503) {
      return {
        redirect: {
          destination: '/maintenance',
          permanent: false,
        },
        props: {
          isMaintenance: true,
        },
      }
    }

    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
      props: {
        isMaintenance: false,
      },
    }
  }
}
